/*
 * APP.TSX - MAIN APPLICATION COMPONENT
 * 
 * Core application wrapper that handles authentication, routing, and global state management.
 * 
 * Key Features:
 * - Authentication and authorization checking
 * - Event bus integration for UI interactions and user actions
 * - Theme management and persistence
 * - Sidebar and chat UI system management with state persistence
 * - Route protection and navigation handling
 * - Global chat context provider integration for responsive grid components
 * 
 * Two-State Chat System:
 * 1. State 'closed': Sidebar + Main content (full width) - no chat visible
 * 2. State 'right-panel': Sidebar + Main content (left) + Chat panel (right)
 * 
 * Chat State Management:
 * - Uses ChatContext for global chat state management (replaces local state)
 * - Chat panel state (closed/right-panel) automatically saved to localStorage via context
 * - State is restored on app reload/restart, maintaining user's preferred chat layout
 * - Animation state is properly initialized based on restored chat state
 * - Seamless user experience with preserved chat positioning across sessions
 * 
 * Responsive Grid Integration:
 * - ChatProvider wrapper enables all grid components to access chat state
 * - Dashboard statistics, Account cards, Contact cards, and Notes grids are chat-aware
 * - Grid columns automatically adjust when chat opens/closes
 * - Smooth transitions provided by responsive utilities
 * 
 * Enhanced Chat Animations:
 * - Smooth slide-in effects when chat panel opens from right
 * - Sophisticated expand/collapse transitions with custom cubic-bezier easing
 * - Hardware-accelerated transforms for optimal 60fps performance
 * - Custom CSS keyframe animations for state transitions
 * - Graceful fade effects for main content when chat expands
 * 
 * Chat Button Behaviors:
 * - Navbar "Open Chat" button: Only transitions from State 1 → State 2
 * - Chat "Expand" button: Opens the dedicated /chat page for a full-screen experience
 * - CHAT_OPEN event bus: Same as navbar button (State 1 → State 2 only)
 * 
 * Event Bus Integration:
 * - Subscribes to CHAT_OPEN events to open chat interface (right panel)
 * - Publishes OPEN_NOTE_MODAL events to trigger New Note modal
 * 
 * UI State Management:
 * - Manages sidebar collapse/hidden state with localStorage persistence
 * - Chat state management delegated to ChatContext
 * - Handles authentication redirects from a single auth bootstrap path
 * - Maintains responsive design with mobile considerations
 *
 * Recent changes:
 * - Constrained the responsive right panel to the mobile flex shrink boundary.
 * - Removed retired AI USER_ACTION subscription after legacy chat tool execution was retired.
 * - Added the AI Inbox top-level route for the API-backed operational queue.
 * - Removed duplicate routed-content top spacing while preserving horizontal and bottom gutters.
 * - Presents mobile navigation as a dismissible overlay instead of squeezing routed content.
 */

import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { isAuthenticated as checkAuth, isAuthorized as checkAuthorized, getUserData } from './services/authService';
import Login from './pages/Login';
import ChatPage from './pages/ChatPage';
import RightPanel from './components/panel/RightPanel';
import Navbar from './components/Navbar';
import Dashboard from './pages/Dashboard';
import Contacts from './pages/Contacts';
import Accounts from './pages/Accounts';
import AccountPage from './pages/Account';
import Sidebar from './components/Sidebar';
import Kanban from './pages/Kanban';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { NotificationProvider } from './context/NotificationContext';
import { EventBusProvider, useEventBus } from './context/EventBusContext';
import { ChatProvider, useChatContext } from './context/ChatContext';
import { EVENTS } from './constants/eventConstants';
import { BREAKPOINTS } from './constants/layoutConstants';
import ContactPage from './pages/Contact';
import { Contact } from './models/Contact';
import { Notification } from './components/common/Notification';
import { TeamProvider } from './context/TeamContext';
import Admin from './pages/Admin';
import Settings from './pages/Settings';
import Help from './pages/Help';
import Notes from './pages/Notes';
import Tasks from './pages/Tasks';
import AIInbox from './pages/AIInbox';
import AccessDenied from './pages/AccessDenied';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { foundationColorTokens, foundationThemes } from './foundation';
import { isAIInboxEnabled } from './constants/featureFlags';

const matchesBreakpoint = (query: string): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.matchMedia(query).matches;
};

const getStoredSidebarPreference = (storageKey: string, fallback: boolean): boolean => {
  if (typeof window === 'undefined') {
    return fallback;
  }

  const saved = localStorage.getItem(storageKey);
  return saved ? JSON.parse(saved) : fallback;
};

// Wrapper component to access location
const AppContent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [selectedContact, setSelectedContact] = useState<Contact | undefined>();
  const [, setAuthRefreshVersion] = useState(0);
  const [isMobileViewport, setIsMobileViewport] = useState<boolean>(() => matchesBreakpoint(BREAKPOINTS.mobile));
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState<boolean>(() => getStoredSidebarPreference('sidebarCollapsed', false));
  const [desktopSidebarHidden, setDesktopSidebarHidden] = useState<boolean>(() => getStoredSidebarPreference('sidebarHidden', false));
  const [mobileSidebarHidden, setMobileSidebarHidden] = useState<boolean>(() => matchesBreakpoint(BREAKPOINTS.mobile));
  const { subscribe, unsubscribe } = useEventBus();
  const {
    chatState,
    chatAnimationState,
    openChat: handleOpenChat,
    closeChat: handleCloseChat,
    activeTab,
    setActiveTab
  } = useChatContext();
  const isChatRoute = location.pathname.startsWith('/chat');
  const isAuthenticated = checkAuth();
  const isAuthorized = checkAuthorized();
  const redirectToAccessDenied = isAuthenticated && !isAuthorized && location.pathname !== '/access-denied';
  const lastNonChatPathRef = useRef<string>(isChatRoute ? '/' : location.pathname + location.search);
  const isSidebarCollapsed = isMobileViewport ? false : desktopSidebarCollapsed;
  const isSidebarHidden = isMobileViewport ? mobileSidebarHidden : desktopSidebarHidden;
  const shouldShowChatPanel = !isChatRoute && (chatState !== 'closed' || chatAnimationState === 'exiting');
  const shouldExpandMainContent = isChatRoute || chatState === 'closed' || chatAnimationState === 'exiting';

  const handleContactSelect = (contact: Contact) => {
    setSelectedContact(contact);
  };

  const handleOpenChatFullPage = () => {
    handleCloseChat();
    navigate('/chat');
  };

  useEffect(() => {
    if (!isChatRoute) {
      lastNonChatPathRef.current = location.pathname + location.search;
    }
  }, [isChatRoute, location.pathname, location.search]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(BREAKPOINTS.mobile);
    const handleMediaChange = (event: MediaQueryListEvent) => {
      setIsMobileViewport(event.matches);

      if (event.matches) {
        setMobileSidebarHidden(true);
      }
    };

    setIsMobileViewport(mediaQuery.matches);
    if (mediaQuery.matches) {
      setMobileSidebarHidden(true);
    }

    mediaQuery.addEventListener('change', handleMediaChange);

    return () => {
      mediaQuery.removeEventListener('change', handleMediaChange);
    };
  }, []);

  const handleNavbarChatToggle = () => {
    if (isChatRoute) {
      const targetPath = lastNonChatPathRef.current || '/';
      navigate(targetPath);
      setTimeout(() => handleOpenChat(), 0);
      return;
    }
    // Toggle logic with tab awareness:
    // - If panel closed → open with Chat tab
    // - If panel open with Editor → switch to Chat tab
    // - If panel open with Chat → close panel
    if (chatState === 'right-panel') {
      if (activeTab === 'editor') {
        setActiveTab('chat');
      } else {
        handleCloseChat();
      }
    } else {
      setActiveTab('chat');
      handleOpenChat();
    }
  };

  const handleSidebarVisibilityToggle = () => {
    if (isMobileViewport) {
      setMobileSidebarHidden((currentValue) => !currentValue);
      return;
    }

    setDesktopSidebarHidden((currentValue) => !currentValue);
  };

  // 1. Chat event subscription
  useEffect(() => {
    subscribe(EVENTS.CHAT_OPEN, handleOpenChat);
    return () => {
      unsubscribe(EVENTS.CHAT_OPEN, handleOpenChat);
    };
  }, [subscribe, unsubscribe, handleOpenChat]);

  // 1.2. Chat open for drop event subscription
  useEffect(() => {
    const handleChatOpenForDrop = () => {
      // Only open chat if it's currently closed - use context function
      handleOpenChat();
    };

    subscribe(EVENTS.CHAT_OPEN_FOR_DROP, handleChatOpenForDrop);
    return () => {
      unsubscribe(EVENTS.CHAT_OPEN_FOR_DROP, handleChatOpenForDrop);
    };
  }, [subscribe, unsubscribe, handleOpenChat]);

  // 1.5. Trigger a rerender when auth state is refreshed outside router navigation.
  useEffect(() => {
    const handleAuthRefresh = () => {
      setAuthRefreshVersion((version) => version + 1);
    };

    window.addEventListener('AUTH_REFRESH', handleAuthRefresh);
    return () => {
      window.removeEventListener('AUTH_REFRESH', handleAuthRefresh);
    };
  }, []);

  // 2. Theme color effect
  useEffect(() => {
    const backgroundColor = isDark ? foundationThemes.dark.backgroundHex : foundationThemes.light.backgroundHex;
    document.body.style.backgroundColor = backgroundColor;
    const themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (themeColorMeta) {
      themeColorMeta.setAttribute('content', backgroundColor);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'theme-color';
      meta.content = backgroundColor;
      document.head.appendChild(meta);
    }
  }, [isDark]);

  // 3. Access Denied redirect effect - always declared, conditionally executed
  useEffect(() => {
    if (redirectToAccessDenied) {
      const userData = getUserData();
      const email = userData?.email || '';
      navigate(`/access-denied?email=${encodeURIComponent(email)}`);
    }
  }, [redirectToAccessDenied, navigate]);

  // 4. Consolidated localStorage persistence for UI state
  useEffect(() => {
    if (isMobileViewport) {
      return;
    }

    // Save sidebar collapsed state
    localStorage.setItem('sidebarCollapsed', JSON.stringify(desktopSidebarCollapsed));

    // Save sidebar hidden state
    localStorage.setItem('sidebarHidden', JSON.stringify(desktopSidebarHidden));

    // Chat state is now handled by ChatContext

  }, [desktopSidebarCollapsed, desktopSidebarHidden, isMobileViewport]);

  // Render login page if not authenticated
  if (!isAuthenticated) {
    return <Login />;
  }

  // Show empty component while redirecting to access denied
  if (redirectToAccessDenied) {
    return null;
  }

  // Special case for access denied page
  if (location.pathname === '/access-denied') {
    return <AccessDenied />;
  }

  // Main application UI
  return (
    <div className={`h-screen flex ${foundationColorTokens.background.canvas}`}>
      {!isSidebarHidden && (
        <>
          {isMobileViewport && (
            <div
              className="fixed inset-0 z-[60] bg-black/40"
              onClick={() => setMobileSidebarHidden(true)}
              aria-hidden="true"
            />
          )}
          <div className={isMobileViewport ? 'fixed inset-y-0 left-0 z-[70] shadow-xl' : ''}>
            <Sidebar
              isCollapsed={isSidebarCollapsed}
              onCollapse={setDesktopSidebarCollapsed}
              showCollapseToggle={!isMobileViewport}
              onDismiss={isMobileViewport ? () => setMobileSidebarHidden(true) : undefined}
              onNavigate={() => {
                if (isMobileViewport) {
                  setMobileSidebarHidden(true);
                }
              }}
            />
          </div>
        </>
      )}

      <div className="flex-1 flex flex-col min-w-0 w-full max-w-full">
        <div className={`sticky top-0 z-40 ${foundationColorTokens.border.subtle} ${foundationColorTokens.background.canvas} w-full`}>
          <div className="w-full flex items-center">
            <Navbar
              onToggleModal={handleSidebarVisibilityToggle}
              onToggleChat={handleNavbarChatToggle}
              currentContact={selectedContact}
              showChatButton={!isChatRoute}
              isNavigationOpen={!isSidebarHidden}
            />
          </div>
        </div>

        <main className={`flex-1 ${foundationColorTokens.background.canvas} w-full max-w-full overflow-hidden ${shouldShowChatPanel ? 'flex' : 'block'}`}>
          <Notification />

          {/* Main Content Panel */}
          <div className={`main-content-transition hw-accel overflow-y-auto px-4 pb-2 pt-0 ${shouldExpandMainContent
            ? 'w-full h-full opacity-100 transform translate-x-0'
            : 'hidden lg:block lg:flex-1 lg:min-w-0 lg:h-full opacity-100 transform translate-x-0'
            }`}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/chat" element={<ChatPage />} />
              <Route path="/chat/:sessionId" element={<ChatPage />} />
              <Route path="/kanban" element={<Kanban />} />
              <Route path="/kanban/:boardId" element={<Kanban />} />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requiresAdmin={true} teamName="User Admin">
                    <Admin />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute requiresAdmin={true} teamName="System Admin">
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route path="/contacts" element={<Contacts />}>
                <Route path="my" element={<Contacts />} />
                <Route path="all" element={<Contacts />} />
                <Route index element={<Contacts />} />
              </Route>
              <Route path="/contacts/:id" element={<ContactPage onContactSelect={handleContactSelect} />} />
              <Route path="/contacts/:id/:tab" element={<ContactPage onContactSelect={handleContactSelect} />} />
              <Route path="/contacts/:id/:tab/:noteId" element={<ContactPage onContactSelect={handleContactSelect} />} />
              <Route path="/accounts" element={<Accounts />}>
                <Route path="my" element={<Accounts />} />
                <Route path="all" element={<Accounts />} />
                <Route index element={<Accounts />} />
              </Route>
              <Route path="/accounts/:id" element={<AccountPage />} />
              <Route path="/accounts/:id/:tab/*" element={<AccountPage />} />
              <Route path="/notes" element={<Notes />} />
              <Route path="/tasks" element={<Tasks />} />
              {isAIInboxEnabled && <Route path="/ai-inbox" element={<AIInbox />} />}
              <Route path="/help" element={<Help />} />
            </Routes>
          </div>

          {/* Right Panel (Chat + Editor tabs) */}
          {shouldShowChatPanel && (
            <div
              className="chat-panel-border hw-accel h-full min-w-0 w-full max-w-full overflow-hidden bg-white dark:bg-gray-800 border border-l border-gray-200 dark:border-gray-700 lg:flex-none lg:w-[26rem] xl:w-[30rem] 2xl:w-[34rem]"
              style={{
                animation: chatAnimationState === 'entering'
                  ? 'slideInFromRight 0.25s ease-out forwards'
                  : chatAnimationState === 'exiting'
                    ? 'slideOutToRight 0.25s ease-out forwards'
                    : 'none',
              }}
            >
              <RightPanel
                onClose={handleCloseChat}
                onOpenFullPage={handleOpenChatFullPage}
              />
            </div>
          )}
        </main>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={6000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <TeamProvider>
        <NotificationProvider>
          <EventBusProvider>
            <ChatProvider>
              <Router>
                <AppContent />
              </Router>
            </ChatProvider>
          </EventBusProvider>
        </NotificationProvider>
      </TeamProvider>
    </ThemeProvider>
  );
};

export default App;
